import { useQuery } from "@tanstack/react-query"
import { Divider, Flex, Radio, Select, Skeleton, Space } from "antd"
import { SetStateAction, useState, useCallback } from "react"
import { useTheme } from "styled-components"
import { ShowMessagePage } from "../../config"
import { getDropboxes } from "../../endpoints"
import { AddressInterface, OrderInfoInterface, PickupInfoInterface } from "../../types"
import { GrayLabel, LabeledBox } from "../GeneralComponents"
import { AddressInput } from "./AddressInput"
import { DeliveryAddress } from "./DeliveryAddress"
import { PickupInfoInput } from "./PickupInfoInput"

export const Shipping = (props: {
    orderInfo: OrderInfoInterface
    setOrderInfo: React.Dispatch<React.SetStateAction<OrderInfoInterface>>
}): JSX.Element => {
    const { setOrderInfo } = props
    const [pickupAddress, setPickupAddress] = useState<AddressInterface>({
        line1: props.orderInfo.shippinginfo?.pickupinfo?.line1 ?? "",
        line2: props.orderInfo.shippinginfo?.pickupinfo?.line2 ?? "",
        city: props.orderInfo.shippinginfo?.pickupinfo?.city ?? "",
        state: props.orderInfo.shippinginfo?.pickupinfo?.state ?? "",
        country: props.orderInfo.shippinginfo?.pickupinfo?.country ?? "USA",
        zip: props.orderInfo.shippinginfo?.pickupinfo?.zip ?? "",
    })

    const setPickupAddress2 = useCallback(
        (value: SetStateAction<AddressInterface | undefined>): void => {
            if (typeof value != "function") {
                setPickupAddress({
                    line1: value?.line1,
                    line2: value?.line2,
                    city: value?.city,
                    state: value?.state,
                    country: value?.country,
                    zip: value?.zip,
                })
                setOrderInfo((orderInfo) => {
                    return {
                        ...orderInfo,
                        shippinginfo: {
                            ...orderInfo.shippinginfo,
                            pickupinfo: {
                                ...orderInfo.shippinginfo?.pickupinfo,
                                line1: value?.line1,
                                line2: value?.line2,
                                city: value?.city,
                                state: value?.state,
                                country: value?.country,
                                zip: value?.zip,
                            },
                        },
                    }
                })
            } else {
                throw Error("not intended caller")
            }
        },
        [setOrderInfo]
    )

    const [pickupInfo, setPickupInfo] = useState<PickupInfoInterface>({
        contactPerson: props.orderInfo.shippinginfo?.pickupinfo?.contactPerson,
        phone: props.orderInfo.shippinginfo?.pickupinfo?.phone,
        specialInstructions: props.orderInfo.shippinginfo?.pickupinfo?.specialInstructions,
        line1: props.orderInfo.shippinginfo?.pickupinfo?.line1 ?? "",
        line2: props.orderInfo.shippinginfo?.pickupinfo?.line2 ?? "",
        city: props.orderInfo.shippinginfo?.pickupinfo?.city ?? "",
        state: props.orderInfo.shippinginfo?.pickupinfo?.state ?? "",
        country: props.orderInfo.shippinginfo?.pickupinfo?.country ?? "USA",
        zip: props.orderInfo.shippinginfo?.pickupinfo?.zip ?? "",
    })
    const setPickupInfo2 = useCallback(
        (value: SetStateAction<PickupInfoInterface>): void => {
            if (typeof value != "function") {
                // console.log(value)
                setPickupInfo({
                    contactPerson: value.contactPerson,
                    phone: value.phone,
                    specialInstructions: value.specialInstructions,
                    line1: value.line1,
                    line2: value.line2,
                    city: value.city,
                    state: value.state,
                    country: value.country,
                    zip: value.zip,
                })
                setOrderInfo((orderInfo) => {
                    return {
                        ...orderInfo,
                        shippinginfo: {
                            ...orderInfo.shippinginfo,
                            pickupinfo: {
                                ...orderInfo.shippinginfo?.pickupinfo,
                                contactPerson: value.contactPerson,
                                phone: value.phone,
                                specialInstructions: value.specialInstructions,
                            },
                        },
                    }
                })
            } else {
                throw Error("not intended caller")
            }
        },
        [setOrderInfo]
    )
    const theme = useTheme()

    const { isPending, isError, data, error, isSuccess } = useQuery({
        queryKey: ["getdropboxes"],
        queryFn: getDropboxes,
    })
    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return ShowMessagePage("Error", error.message)
    }
    if (isSuccess) {
        if (data.code === 1000) {
            const DropBox = data.dropboxes.map((box) => {
                return { value: box.id, label: "( #" + box.id + " ) " + box.label }
            })
            // consolelog('dropbox is',data.dropboxes)

            return (
                <div>
                    {props.orderInfo.productid != null && (
                        <LabeledBox title="Shipping Information" border>
                            <div
                                style={{
                                    padding: "10px",
                                }}
                            >
                                <Radio.Group
                                    onChange={(e) => {
                                        setOrderInfo({
                                            ...props.orderInfo,
                                            shippinginfo: {
                                                ...props.orderInfo.shippinginfo,
                                                method: e.target.value,
                                            },
                                        })
                                        // setShippingMethod(e.target.value)
                                    }}
                                    value={props.orderInfo.shippinginfo?.method}
                                >
                                    <Space direction="vertical">
                                        <Radio value={"dropbox"}>
                                            {" "}
                                            <div style={{ width: "8em" }}>
                                                <span
                                                    className="material-symbols-outlined"
                                                    style={{
                                                        verticalAlign: "middle",
                                                        fontSize: "1rem",
                                                        color:
                                                            props.orderInfo.shippinginfo?.method === "dropbox"
                                                                ? theme.primary
                                                                : theme.forth,
                                                    }}
                                                >
                                                    package_2
                                                </span>
                                                <span
                                                    style={{
                                                        verticalAlign: "middle",
                                                        marginLeft: "1em",
                                                        fontWeight:
                                                            props.orderInfo.shippinginfo?.method === "dropbox"
                                                                ? "bold"
                                                                : "normal",
                                                    }}
                                                >
                                                    Drop Box
                                                </span>
                                            </div>
                                        </Radio>
                                        <Radio value={"pickup"}>
                                            <div style={{ width: "8em" }}>
                                                <span
                                                    className="material-symbols-outlined"
                                                    style={{
                                                        verticalAlign: "middle",
                                                        fontSize: "1rem",
                                                        color:
                                                            props.orderInfo.shippinginfo?.method === "pickup"
                                                                ? theme.primary
                                                                : theme.forth,
                                                    }}
                                                >
                                                    boy
                                                </span>
                                                <span
                                                    style={{
                                                        verticalAlign: "middle",
                                                        marginLeft: "1em",
                                                        fontWeight:
                                                            props.orderInfo.shippinginfo?.method === "pickup"
                                                                ? "bold"
                                                                : "normal",
                                                    }}
                                                >
                                                    Pick Up
                                                </span>
                                            </div>
                                        </Radio>
                                        <Radio value={"mail"}>
                                            <div style={{ width: "12em" }}>
                                                <span
                                                    className="material-symbols-outlined"
                                                    style={{
                                                        verticalAlign: "middle",
                                                        fontSize: "1rem",
                                                        color:
                                                            props.orderInfo.shippinginfo?.method === "mail"
                                                                ? theme.primary
                                                                : theme.forth,
                                                    }}
                                                >
                                                    mail
                                                </span>
                                                <span
                                                    style={{
                                                        verticalAlign: "middle",
                                                        marginLeft: "0.5em",
                                                        fontWeight:
                                                            props.orderInfo.shippinginfo?.method === "mail"
                                                                ? "bold"
                                                                : "normal",
                                                    }}
                                                >
                                                    Hand delivery / Mail
                                                </span>
                                            </div>
                                        </Radio>
                                    </Space>
                                </Radio.Group>
                            </div>
                            <Divider />
                            <div
                                style={{
                                    padding: "10px",
                                    minHeight: "18em",
                                }}
                            >
                                {props.orderInfo.shippinginfo?.method === "dropbox" && (
                                    <Flex vertical className={"fade-in"}>
                                        <GrayLabel>Please select the dropbox:</GrayLabel>
                                        <Select
                                            placeholder="Select a dropbox"
                                            onChange={(value) => {
                                                // console.log(value)
                                                setOrderInfo({
                                                    ...props.orderInfo,
                                                    shippinginfo: {
                                                        ...props.orderInfo.shippinginfo,
                                                        dropbox: data.dropboxes.find(
                                                            (box) => box.id === value
                                                        ),  
                                                    },
                                                })
                                            }}
                                            optionFilterProp="label"
                                            showSearch
                                            value={props.orderInfo.shippinginfo.dropbox?.id}
                                            options={DropBox}
                                        />
                                    </Flex>
                                )}
                                {props.orderInfo.shippinginfo?.method === "pickup" && (
                                    <Flex vertical gap="small" className={"fade-in"}>
                                        <div>Pick up from: (currently servicing Boston metropolitan area)</div>
                                        <AddressInput address={pickupAddress} setAddress={setPickupAddress2} />
                                        <PickupInfoInput pickupInfo={pickupInfo} setPickupInfo={setPickupInfo2} />
                                    </Flex>
                                )}
                                {props.orderInfo.shippinginfo?.method === "mail" && (
                                    <DeliveryAddress className={"fade-in"} />
                                )}
                            </div>
                        </LabeledBox>
                    )}
                </div>
            )
        }
    }
    return <> </>
}
